import { useCallback, useEffect, useMemo, useState } from "react";
import { BarChart3, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, openPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  EmptyState,
  ErrorState,
  Field,
  Loading,
  PageHeader,
  Surface,
} from "@/components/ds";

const GROUPS = [
  ["produtos", "Produtos"],
  ["estoque", "Estoque"],
  ["parceiros", "Parceiros"],
  ["comercial", "Comercial"],
];

const hoje = () => new Date().toISOString().slice(0, 10);
const iso = (d) => d.toISOString().slice(0, 10);

// Mesmos atalhos de período usados no Financeiro.
const PERIODS = {
  hoje: () => [hoje(), hoje()],
  "7dias": () => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return [iso(d), hoje()];
  },
  mes: () => {
    const n = new Date();
    return [iso(new Date(n.getFullYear(), n.getMonth(), 1)), hoje()];
  },
  anterior: () => {
    const n = new Date();
    return [
      iso(new Date(n.getFullYear(), n.getMonth() - 1, 1)),
      iso(new Date(n.getFullYear(), n.getMonth(), 0)),
    ];
  },
  "3meses": () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return [iso(d), hoje()];
  },
  ano: () => {
    const n = new Date();
    return [iso(new Date(n.getFullYear(), 0, 1)), hoje()];
  },
};

const PERIOD_LABELS = [
  ["hoje", "Hoje"],
  ["7dias", "7 dias"],
  ["mes", "Este mês"],
  ["anterior", "Mês anterior"],
  ["3meses", "3 meses"],
  ["ano", "Este ano"],
  ["custom", "Personalizado"],
];

export default function Reports() {
  const [group, setGroup] = useState("produtos");
  const [period, setPeriod] = useState("mes");
  const [range, setRange] = useState(PERIODS.mes());
  const [category, setCategory] = useState("");
  const [partnerId, setPartnerId] = useState("");
  const [cats, setCats] = useState([]);
  const [partners, setPartners] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const params = useMemo(() => {
    const p = { group, start: range[0], end: range[1] };
    if (category) p.category = category;
    if (partnerId) p.partner_id = partnerId;
    return p;
  }, [group, range, category, partnerId]);

  const load = useCallback(async () => {
    setError("");
    setBusy(true);
    try {
      const { data: d } = await api.get("/reports", { params });
      setData(d);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([api.get("/categories"), api.get("/partners")]);
        setCats(c.data);
        setPartners(p.data);
      } catch {
        /* filtros opcionais: a tela funciona sem eles */
      }
    })();
  }, []);

  const pickPeriod = (key) => {
    setPeriod(key);
    if (PERIODS[key]) setRange(PERIODS[key]());
  };

  const pdf = async (report) => {
    try {
      const q = new URLSearchParams(params).toString();
      await openPdf(`/reports/pdf?report=${report}&${q}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data && busy) return <Loading label="Montando relatórios..." />;

  const showCategory = group === "produtos" || group === "estoque";
  const showPartner = group === "parceiros";

  return (
    <div className="space-y-8" data-testid="reports-page">
      <PageHeader
        title="Relatórios"
        subtitle="Os números vêm direto dos módulos do sistema — nada é recalculado por aqui."
      />

      <Tabs value={group} onValueChange={setGroup}>
        <TabsList className="flex w-full flex-wrap justify-start gap-1">
          {GROUPS.map(([k, label]) => (
            <TabsTrigger key={k} value={k} data-testid={`reports-tab-${k}`}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Surface className="space-y-4 p-5" data-testid="reports-filters">
        <div className="flex flex-wrap gap-1">
          {PERIOD_LABELS.map(([k, label]) => (
            <button
              key={k}
              onClick={() => pickPeriod(k)}
              data-testid={`reports-period-${k}`}
              className={`tr-chip ${period === k ? "tr-chip-active" : ""}`}
            >
              {label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="grid gap-3 sm:grid-cols-2 sm:max-w-md">
            <Field label="De">
              <Input
                type="date"
                value={range[0]}
                onChange={(e) => setRange([e.target.value, range[1]])}
                data-testid="reports-start-input"
              />
            </Field>
            <Field label="Até">
              <Input
                type="date"
                value={range[1]}
                onChange={(e) => setRange([range[0], e.target.value])}
                data-testid="reports-end-input"
              />
            </Field>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2 sm:max-w-xl">
          {showCategory && (
            <Field label="Categoria">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="reports-category-select"
                className="h-10 w-full rounded-lg border border-white/10 bg-secondary/60 px-3 text-sm"
              >
                <option value="">Todas</option>
                {cats.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {showPartner && (
            <Field label="Parceiro">
              <select
                value={partnerId}
                onChange={(e) => setPartnerId(e.target.value)}
                data-testid="reports-partner-select"
                className="h-10 w-full rounded-lg border border-white/10 bg-secondary/60 px-3 text-sm"
              >
                <option value="">Todos</option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>
      </Surface>

      {busy && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Atualizando...
        </p>
      )}

      <div className="space-y-6">
        {(data?.reports || []).map((r) => (
          <ReportTable key={r.key} report={r} onPdf={() => pdf(r.key)} />
        ))}
      </div>
    </div>
  );
}

const ReportTable = ({ report, onPdf }) => (
  <Surface className="overflow-hidden p-5" data-testid={`report-${report.key}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-display text-base font-medium">{report.title}</p>
        {report.note && (
          <p className="mt-0.5 text-xs text-muted-foreground">{report.note}</p>
        )}
      </div>
      {report.rows.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onPdf}
          data-testid={`report-pdf-btn-${report.key}`}
          className="gap-1.5 rounded-full text-xs text-muted-foreground hover:text-foreground"
        >
          <FileText className="h-3.5 w-3.5" /> Gerar PDF
        </Button>
      )}
    </div>

    {report.rows.length === 0 ? (
      <div className="mt-4">
        <EmptyState
          icon={BarChart3}
          title="Nenhum dado encontrado para os filtros selecionados."
          description="Ajuste o período ou os filtros acima."
        />
      </div>
    ) : (
      <div className="mt-4 w-full max-w-full overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-xs text-muted-foreground">
              {report.columns.map((c, i) => (
                <th
                  key={c}
                  className={`pb-2 pr-3 font-normal ${i > 0 ? "text-right" : ""}`}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {report.rows.map((row, ri) => (
              <tr key={ri} data-testid={`report-${report.key}-row-${ri}`}>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className={`py-2 pr-3 ${
                      ci > 0 ? "text-right font-num" : "min-w-0 break-words"
                    }`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Surface>
);
